import React from "react";
import {
  Avatar,
  createStyles,
  Divider,
  Grid,
  Link,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Facebook, Instagram, Pinterest, Twitter, YouTube } from "@material-ui/icons";
import { PFXHyperlinks, PFXSocials, PFXSocialsPages } from "../../models/Interfaces";
import { useSelector } from "react-redux";
import { ErrorType, selectCompany, selectError } from "../../redux/slices/cleverlynkSlice";

import { useTranslation } from "react-i18next";
import "../../i18n/config";
import FooterContactInfo from "./FooterContactInfo";

export const SIC_LIST = ["wsTzRW_knFkRLOy1ixNhq", "Obpct3kqJgOtZxGgk2Lgo"];

interface IFooter {
  hyperlinks: PFXHyperlinks[];
  socials: PFXSocials[];
  textColor: string;
  backgroundColor: string;
  enable: boolean;
}

const Footer = ({ hyperlinks, socials, textColor, backgroundColor, enable }: IFooter) => {
  const company = useSelector(selectCompany);
  const error = useSelector(selectError);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const height = React.useMemo(() => Math.max(200, window.innerHeight / 3), []);

  const styles = React.useMemo(
    () => ({
      textColor,
      backgroundColor,
      alignment: "center",
    }),
    [textColor, backgroundColor]
  );

  const { t } = useTranslation("socialfooter");

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        margin: 0,
        padding: isMobile ? 10 : "75px 100px",
        paddingBottom: 30,
        width: "100%",
        backgroundColor: styles.backgroundColor,
        color: styles.textColor,
        minHeight: isMobile ? "" : height,
      },
      hyperlinksText: {
        cursor: "pointer",
        fontSize: "1rem",
        color: `${styles.textColor}c4`,
        textDecoration: "none",
        paddingTop: 10,
      },
    })
  );
  const classes = useStyles();

  const matchPageWithIcon = (page: PFXSocialsPages | undefined) => {
    switch (page) {
      case PFXSocialsPages.INSTAGRAM:
        return <Instagram />;
      case PFXSocialsPages.FACEBOOK:
        return <Facebook />;
      case PFXSocialsPages.PINTEREST:
        return <Pinterest />;
      case PFXSocialsPages.TWITTER:
        return <Twitter />;
      case PFXSocialsPages.YOUTUBE:
        return <YouTube />;
    }
  };

  const handleSocialClick = url => {
    var win = window.open(url, "_blank");
    win?.focus();
  };

  return enable && (!error || error?.type === ErrorType.STORE_CLOSED) ? (
    <Grid container className={classes.root} spacing={isMobile ? 2 : 1} alignContent="space-between">
      <Grid item container xs={12} justifyContent="space-evenly">
        <Grid item xs={12} md={4} container justifyContent="center" alignItems="center">
          <img src={company?.logoUrl} alt="logo" style={{ maxWidth: "100%", maxHeight: "100px" }} />
        </Grid>
        <Grid item xs={8} container>
          {hyperlinks.length === 0 || (hyperlinks.length === 1 && hyperlinks[0].name === "") ? null : (
            <Grid
              item
              xs={12}
              md={4}
              container
              direction="column"
              justifyContent={"flex-start"}
              // alignContent={isMobile ? (styles.alignment as any) ?? "center" : "flex-start"}
              style={{
                paddingLeft: isMobile ? 35 : 0,
                paddingRight: isMobile ? 35 : 0,
                paddingTop: isMobile ? 35 : 0,
              }}>
              <Grid item>
                <Typography style={{ color: `${styles.textColor}f3`, marginBottom: 5 }} variant="h6">
                  Sobre Nosotros
                </Typography>
              </Grid>
              {hyperlinks.map(h => (
                <Grid item style={{ marginBottom: 7, marginTop: 3, marginLeft: isMobile ? 5 : 0 }} key={h.name}>
                  <Typography
                    className={classes.hyperlinksText}
                    component="a"
                    href={h?.url?.includes("http") ? h.url : `https://${h.url}`}
                    rel="noopener noreferrer"
                    target="_blank">
                    {h.name}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}
          <Grid
            item
            xs={12}
            md={4}
            container
            direction="column"
            justifyContent={"flex-start"}
            alignContent={isMobile ? (styles.alignment as any) ?? "center" : "flex-start"}
            style={{ paddingLeft: isMobile ? 35 : 0, paddingRight: isMobile ? 35 : 0, paddingTop: isMobile ? 35 : 0 }}>
            <Grid item>
              <Typography style={{ color: `${styles.textColor}f3`, marginBottom: 5 }} variant="h6">
                {t("contactUs")}
              </Typography>
            </Grid>
            <FooterContactInfo color={`${styles.textColor}c4`} />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            container
            justifyContent="center"
            alignItems={!isMobile ? "center" : "flex-start"}
            style={{ paddingTop: isMobile ? 35 : 0 }}>
            {socials.map(s => (
              <Grid item style={{ paddingRight: 10 }} key={s.url}>
                <Avatar
                  onClick={() => handleSocialClick(s.url)}
                  style={{ color: styles.backgroundColor, backgroundColor: styles.textColor, cursor: "pointer" }}>
                  {matchPageWithIcon(s.page)}
                </Avatar>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        container
        xs={12}
        justifyContent="center"
        spacing={3}
        style={{ margin: 0, maxHeight: 165, marginTop: 45 }}>
        <Grid item xs={12} md={3} style={{ paddingBottom: 0 }}></Grid>
        <Grid item style={{ paddingBottom: 0 }}>
          <Typography variant="caption" style={{ color: `${styles.textColor} !important` }}>
            <Link
              color="inherit"
              rel="noopener noreferrer"
              href={"https://cleverlynk-imgs.s3.amazonaws.com/Cleverlynk/Politica+de+privacidad+landing+page.pdf"}
              target="_blank">
              {t("privacyPolicy")}
            </Link>
          </Typography>
        </Grid>
        {company?.termsURL && (
          <Grid item style={{ paddingBottom: 0 }}>
            <Typography variant="caption" style={{ color: `${styles.textColor} !important` }}>
              <Link color="inherit" rel="noopener noreferrer" href={company?.termsURL} target="_blank">
                {t("termsAndConditions")}
              </Link>
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} md={3} style={{ paddingBottom: 0 }}></Grid>
        <Grid item xs={12} style={{ paddingBottom: 0 }} container justifyContent="center">
          <Typography
            variant="overline"
            style={{ fontSize: "0.5rem", borderTop: `1pt solid ${styles.textColor}`, paddingTop: 6 }}>
            Powered by Cleverlynk
          </Typography>
        </Grid>
        {SIC_LIST.includes(company?.id ?? "") && (
          <>
            <Grid item xs={12}>
              <Divider style={{ backgroundColor: styles.textColor }} />
            </Grid>
            <Grid item xs={12} container justifyContent="space-between" style={{ padding: "0 16px" }}>
              <Grid item>
                {(company as any)?.alegra?.identificationType === "NIT" && (
                  <>
                    <Typography
                      variant="caption"
                      style={{ fontSize: "0.6rem", paddingRight: "24px", color: styles.textColor }}>
                      {(company as any).alegra.businessName}
                    </Typography>
                    <Typography variant="caption" style={{ fontSize: "0.6rem", color: styles.textColor }}>
                      {"NIT: " + (company as any).alegra.identificationNumber}
                    </Typography>
                  </>
                )}
              </Grid>
              <Grid item>
                <Link
                  variant="caption"
                  style={{ fontSize: "0.6rem", color: styles.textColor }}
                  underline="none"
                  href="https://www.sic.gov.co/"
                  target="_blank"
                  rel="noopener noreferrer">
                  Superintendencia de Industria y Comercio
                </Link>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  ) : null;
};

export default Footer;
